/**
 * Footer Component
 * 
 * A responsive footer component that displays company information, contact details,
 * quick links, and legal information. Includes social media links and copyright notice.
 * 
 * Features:
 * - Responsive grid layout
 * - Company logo and tagline
 * - Contact information
 * - Quick navigation links
 * - Legal links
 * - Social media integration
 * - Copyright notice
 */

'use client';

import Link from 'next/link';
import { Container } from '@/components/ui/container';
import { Separator } from '@/components/ui/separator';
import { Mail, Phone, BotMessageSquare, Linkedin } from 'lucide-react';
import { BrandLogo } from '@/components/brand-logo';

/**
 * Footer Component
 * 
 * @component
 * @returns {JSX.Element} The footer component
 * 
 * @example
 * <Footer />
 */
export function Footer() {
	return (
		<footer className="relative bg-background border-t z-10">
			<Container>
				<div className="py-12 grid grid-cols-1 md:grid-cols-4 gap-16">
					{/* Company Information Section */}
					<div>
						<Link href="/" className="block -mt-3">
							<BrandLogo
								className="h-16 w-auto sm:h-20 transition-transform hover:scale-105"
								priority
							/>
						</Link>
						<p className="text-sm text-muted-foreground mt-4">
							Smart Systems. Local Focus. Real Results.
						</p>
						<div className="flex space-x-4 mt-4">
							<a
								href="https://www.linkedin.com/company/joviancloudworksconsulting"
								target="_blank"
								rel="noopener noreferrer"
								className="text-muted-foreground hover:text-orange-500"
							>
								<Linkedin className="w-5 h-5" />
								<span className="sr-only">LinkedIn</span>
							</a>
						</div>
					</div>

					{/* Contact Information Section */}
					<div>
						<h3 className="text-base font-semibold mb-4">Contact Information</h3>
						<ul className="space-y-3 text-sm">
							<li className="flex items-center space-x-2 text-muted-foreground">
								<Mail className="w-4 h-4" />
								<span>contact@joviancloudworks.com</span>
							</li>
							<li className="flex items-center space-x-2 text-muted-foreground">
								<Phone className="w-4 h-4" />
								<span>+1 (626) 234-9057</span>
							</li>
						</ul>
					</div>

					{/* Quick Links Section */}
					<div>
						<h3 className="text-base font-semibold mb-4">Quick Links</h3>
						<ul className="space-y-2 text-sm">
							<li>
								<Link href="/services" className="text-muted-foreground hover:text-orange-500">
									Fractional CTO Services
								</Link>
							</li>
							<li>
								<Link href="/tools/automation-roi-calculator" className="text-muted-foreground hover:text-orange-500">
									ROI Calculator
								</Link>
							</li>
							<li>
								<Link href="/tools/tech-audit-assessment" className="text-muted-foreground hover:text-orange-500">
									Tech Audit
								</Link>
							</li>
							<li>
								<Link
									href="https://app.hellobonsai.com/f/0036f2e3832fcb0"
									target="_blank"
									rel="noopener noreferrer"
									className="text-muted-foreground hover:text-orange-500"
								>
									Contact Us
								</Link>
							</li>
						</ul>
					</div>

					{/* Legal Links Section */}
					<div>
						<h3 className="text-base font-semibold mb-4">Legal</h3>
						<ul className="space-y-2 text-sm">
							<li>
								<Link href="/privacy" className="text-muted-foreground hover:text-orange-500">
									Privacy Policy
								</Link>
							</li>
							<li>
								<Link href="/terms" className="text-muted-foreground hover:text-orange-500">
									Terms of Service
								</Link>
							</li>
							<li>
								<Link href="/cookies" className="text-muted-foreground hover:text-orange-500">
									Cookie Policy
								</Link>
							</li>
						</ul>
					</div>
				</div>

				<Separator />

				{/* Copyright Section */}
				<div className="py-6 text-center text-sm text-muted-foreground">
					<p>© {new Date().getFullYear()} JovianCloudWorks. All rights reserved.</p>
				</div>
			</Container>
		</footer>
	);
}
